import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";

import styles from './SupportChatPage.module.scss';
import arrowLeft from '../../constants/images/arrow-left.svg';
import sendIcon from '../../constants/images/detailsOrder/send.svg';
import uploadIcon from '../../constants/images/upload-icon.svg';
import {supportActions} from "../../store/slices/supportSlice";
import {MAIN_ROUTES} from "../../routing/main_routes";
import Loader from "../../components/Loader/Loader";

const STATUS_LABELS = {
    open: 'Відкрито',
    in_progress: 'В роботі',
    resolved: 'Вирішено',
    closed: 'Закрито',
};

const SENDER_ROLE_LABEL = {
    admin: 'Підтримка',
    system: 'Система',
    customer: 'Клієнт',
    cleaner: 'Клінер',
};

const SupportChatPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {pathname} = useLocation();

    // extract ticketId from URL /support/chat/:id
    const pathArray = pathname.split('/');
    let ticketId;
    if ((typeof +pathArray[pathArray.length - 1] === 'number') && (+pathArray[pathArray.length - 1] !== 0)) {
        ticketId = +pathArray[pathArray.length - 1];
    } else {
        ticketId = +pathArray[pathArray.length - 2];
    }

    const {activeTicket, messages, status, messagesStatus} = useSelector((state) => state.support);

    const isCleaner = !!JSON.parse(localStorage.getItem('isCleaner'));
    const myRole = isCleaner ? 'cleaner' : 'customer';

    const [composerText, setComposerText] = useState('');
    const [attachedFiles, setAttachedFiles] = useState([]);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const composerRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, []);

    // initial load
    useEffect(() => {
        if (!ticketId) return;

        const loadData = async () => {
            try {
                await dispatch(supportActions.getTicket({id: ticketId}));
                await dispatch(supportActions.listMessages({ticketId}));
                dispatch(supportActions.markRead({ticketId}));
            } catch (error) {
                console.error(error);
            }
        };

        loadData();
    }, [dispatch, ticketId]);

    // poll messages every 5s
    useEffect(() => {
        if (!ticketId) return;

        const pollInterval = setInterval(() => {
            dispatch(supportActions.listMessages({ticketId}));
            dispatch(supportActions.markRead({ticketId}));
        }, 5000);

        return () => clearInterval(pollInterval);
    }, [dispatch, ticketId]);

    // scroll to bottom when messages update
    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    const handleSend = async () => {
        const text = composerText.trim();
        if (!text && attachedFiles.length === 0) return;

        setComposerText('');
        const filesToSend = [...attachedFiles];
        setAttachedFiles([]);

        try {
            await dispatch(supportActions.postMessage({
                ticketId,
                body: text,
                attachments: filesToSend.length > 0 ? filesToSend : undefined,
            }));
        } catch (error) {
            console.error(error);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        setAttachedFiles((prev) => [...prev, ...files]);
        e.target.value = '';
    };

    const removeAttachment = (index) => {
        setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleTimeString('uk-UA', {hour: '2-digit', minute: '2-digit'});
    };

    const getMessageStyle = (senderRole) => {
        if (senderRole === 'system') return 'system';
        if (senderRole === myRole) return 'mine';
        if (senderRole === 'admin') return 'admin';
        return 'other';
    };

    const getBubbleStyle = (senderRole) => {
        if (senderRole === 'system') return 'system';
        if (senderRole === myRole) return 'mine';
        if (senderRole === 'admin') return 'admin';
        return 'other';
    };

    if (status === 'loading' && !activeTicket) {
        return <Loader/>;
    }

    const ticketStatus = activeTicket?.status || 'open';
    const ticketTitle = activeTicket?.subject || activeTicket?.category_display || 'Звернення';
    const isClosed = ticketStatus === 'closed' || ticketStatus === 'resolved';

    return (
        <div className={styles.chatPage_wrapper}>
            <div className={styles.chatPage}>
                {/* Header */}
                <div className={styles.chatPage_header}>
                    <button
                        className={styles.chatPage_header_back}
                        onClick={() => navigate(MAIN_ROUTES.SUPPORT)}
                        aria-label="Назад"
                    >
                        <img src={arrowLeft} alt="назад"/>
                    </button>

                    <div className={styles.chatPage_header_info}>
                        <p className={styles.chatPage_header_info_title}>{ticketTitle}</p>
                        <p className={styles.chatPage_header_info_subtitle}>
                            {activeTicket?.category_display || ''}
                        </p>
                    </div>

                    <span
                        className={`${styles.chatPage_header_status} ${styles[`chatPage_header_status--${ticketStatus}`]}`}
                    >
                        {activeTicket?.status_display || STATUS_LABELS[ticketStatus] || ticketStatus}
                    </span>
                </div>

                {/* Messages */}
                <div className={styles.chatPage_messages}>
                    {messagesStatus === 'loading' && messages.length === 0 && (
                        <Loader/>
                    )}

                    {messages.map((msg) => {
                        const msgStyle = getMessageStyle(msg.sender_role);
                        const bubbleStyle = getBubbleStyle(msg.sender_role);
                        const isSystem = msg.sender_role === 'system';
                        const showSenderLabel = !isSystem && msgStyle !== 'mine';

                        return (
                            <div
                                key={msg.id}
                                className={`${styles.message} ${styles[`message--${msgStyle}`]}`}
                            >
                                <div className={`${styles.message_bubble} ${styles[`message_bubble--${bubbleStyle}`]}`}>
                                    {showSenderLabel && (
                                        <p className={styles.message_bubble_senderLabel}>
                                            {SENDER_ROLE_LABEL[msg.sender_role] || msg.sender_role}
                                            {msg.sender?.first_name ? ` (${msg.sender.first_name})` : ''}
                                        </p>
                                    )}

                                    {msg.body && <p>{msg.body}</p>}

                                    {msg.attachments && msg.attachments.length > 0 && (
                                        <div className={styles.message_bubble_attachments}>
                                            {msg.attachments.map((att) => (
                                                <a
                                                    key={att.id}
                                                    href={att.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.message_bubble_attachments_link}
                                                >
                                                    {att.original_name || 'Вкладення'}
                                                </a>
                                            ))}
                                        </div>
                                    )}

                                    {!isSystem && (
                                        <p className={styles.message_bubble_time}>
                                            {formatTime(msg.created_on)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    <div ref={messagesEndRef}/>
                </div>

                {/* Composer */}
                <div className={styles.chatPage_composerWrapper}>
                    {attachedFiles.length > 0 && (
                        <div className={styles.chatPage_composer_attachPreview}>
                            {attachedFiles.map((file, index) => (
                                <span key={index} className={styles.chatPage_composer_attachPreview_item}>
                                    {file.name}
                                    <button
                                        type="button"
                                        className={styles.chatPage_composer_attachPreview_item_remove}
                                        onClick={() => removeAttachment(index)}
                                        aria-label="Видалити вкладення"
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {!isClosed && (
                        <div className={styles.chatPage_composer}>
                            <button
                                type="button"
                                className={styles.chatPage_composer_attach}
                                onClick={() => fileInputRef.current?.click()}
                                aria-label="Прикріпити файл"
                            >
                                <img src={uploadIcon} alt="прикріпити"/>
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                className={styles.chatPage_composer_fileInput}
                                onChange={handleFileChange}
                                aria-hidden="true"
                            />

                            <textarea
                                ref={composerRef}
                                className={styles.chatPage_composer_input}
                                placeholder="Написати повідомлення..."
                                value={composerText}
                                onChange={(e) => setComposerText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                rows={1}
                            />

                            <button
                                type="button"
                                className={styles.chatPage_composer_send}
                                onClick={handleSend}
                                disabled={messagesStatus === 'sending' || (!composerText.trim() && attachedFiles.length === 0)}
                                aria-label="Надіслати"
                            >
                                <img src={sendIcon} alt="надіслати"/>
                            </button>
                        </div>
                    )}

                    {isClosed && (
                        <div className={styles.chatPage_composer}>
                            <p style={{color: '#595959', fontSize: '13px', padding: '12px 0', textAlign: 'center', width: '100%'}}>
                                Це звернення закрито. Для нового запиту створіть окреме звернення.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupportChatPage;
